import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "../../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Switch } from "../../../../components/ui/switch";
import {
  getAvailableSkills,
  getMeetingAutomationPreferences,
  updateMeetingAutomationPreferences,
  type AvailableSkill,
  type MeetingAutomationPreferences,
  type MeetingAutomationPreferencesUpdate,
} from "../../../../services/meetingAutomationEndpoints";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import notify from "../../../../utils/notify";
import NotificationChannelSelector from "./NotificationChannelSelector";

const MINUTES_OPTIONS = [
  { value: "15", label: "15 minutes before" },
  { value: "30", label: "30 minutes before" },
  { value: "60", label: "1 hour before" },
  { value: "120", label: "2 hours before" },
];

/** Null-safe skill ID for select — maps null to "default" */
const SKILL_DEFAULT = "__default__";

const SkillSelector = ({
  value,
  onChange,
  skills,
  isLoading,
}: {
  value: string | null | undefined;
  onChange: (id: string | null) => void;
  skills: AvailableSkill[];
  isLoading: boolean;
}) => {
  const selectValue = value || SKILL_DEFAULT;

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5" />
        Skill
      </label>
      <Select
        value={selectValue}
        onValueChange={(v) => onChange(v === SKILL_DEFAULT ? null : v)}
      >
        <SelectTrigger className="h-9 w-full max-w-[320px]">
          <SelectValue placeholder={isLoading ? "Loading skills..." : "Select skill"} />
        </SelectTrigger>
        <SelectContent>
          {skills.map((skill) => (
            <SelectItem
              key={skill.id ?? SKILL_DEFAULT}
              value={skill.id ?? SKILL_DEFAULT}
            >
              {skill.display_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        Choose which skill to use for generating the brief. Default uses the built-in meeting prep skill.
      </p>
    </div>
  );
};

const MeetingAutomationSettingsPage = () => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const queryClient = useQueryClient();

  // Pre-meeting state
  const [preMeetingEnabled, setPreMeetingEnabled] = useState(false);
  const [preMeetingChannel, setPreMeetingChannel] = useState("none");
  const [preMeetingSlackChannelId, setPreMeetingSlackChannelId] = useState("");
  const [preMeetingMinutesBefore, setPreMeetingMinutesBefore] = useState("30");
  const [preMeetingIncludeInternal, setPreMeetingIncludeInternal] = useState(false);
  const [preMeetingGuidance, setPreMeetingGuidance] = useState("");
  const [preMeetingSkillId, setPreMeetingSkillId] = useState<string | null>(null);

  // Post-meeting summary state
  const [postSummaryEnabled, setPostSummaryEnabled] = useState(false);
  const [postSummaryChannel, setPostSummaryChannel] = useState("none");
  const [postSummarySlackChannelId, setPostSummarySlackChannelId] = useState("");
  const [postSummarySkillId, setPostSummarySkillId] = useState<string | null>(null);

  // Post-meeting followup state
  const [postFollowupEnabled, setPostFollowupEnabled] = useState(false);
  const [postFollowupChannel, setPostFollowupChannel] = useState("none");
  const [postFollowupSlackChannelId, setPostFollowupSlackChannelId] = useState("");
  const [postFollowupIncludeInternal, setPostFollowupIncludeInternal] = useState(false);
  const [postFollowupGuidance, setPostFollowupGuidance] = useState("");
  const [postFollowupSkillId, setPostFollowupSkillId] = useState<string | null>(null);

  // Fetch preferences
  const settingsQuery = useQuery<MeetingAutomationPreferences | null>({
    queryKey: ["meeting-automation-preferences", workspace?.id],
    enabled: !!workspace?.id,
    queryFn: async () => {
      if (!workspace?.id) return null;
      try {
        return await getMeetingAutomationPreferences({
          workspace_id: workspace.id,
        });
      } catch {
        return null;
      }
    },
  });

  // Fetch available skills
  const skillsQuery = useQuery<AvailableSkill[]>({
    queryKey: ["meeting-automation-skills", workspace?.id],
    enabled: !!workspace?.id,
    queryFn: async () => {
      if (!workspace?.id) return [];
      try {
        return await getAvailableSkills({ workspace_id: workspace.id });
      } catch {
        return [];
      }
    },
    staleTime: 1000 * 60 * 5,
  });

  const skills = useMemo(() => skillsQuery.data ?? [], [skillsQuery.data]);

  useEffect(() => {
    if (settingsQuery.data) {
      const d = settingsQuery.data;
      setPreMeetingEnabled(d.pre_meeting_enabled);
      setPreMeetingChannel(d.pre_meeting_notify_channel || "none");
      setPreMeetingSlackChannelId(d.pre_meeting_slack_channel_id || "");
      setPreMeetingMinutesBefore(String(d.pre_meeting_minutes_before || 30));
      setPreMeetingIncludeInternal(d.pre_meeting_include_internal);
      setPreMeetingGuidance(d.pre_meeting_guidance || "");
      setPreMeetingSkillId(d.pre_meeting_custom_skill_id || null);

      setPostSummaryEnabled(d.post_meeting_summary_enabled);
      setPostSummaryChannel(d.post_meeting_summary_channel || "none");
      setPostSummarySlackChannelId(d.post_meeting_summary_slack_channel_id || "");
      setPostSummarySkillId(d.post_meeting_summary_custom_skill_id || null);

      setPostFollowupEnabled(d.post_meeting_followup_enabled);
      setPostFollowupChannel(d.post_meeting_followup_channel || "none");
      setPostFollowupSlackChannelId(d.post_meeting_followup_slack_channel_id || "");
      setPostFollowupIncludeInternal(d.post_meeting_include_internal);
      setPostFollowupGuidance(d.post_meeting_guidance || "");
      setPostFollowupSkillId(d.post_meeting_followup_custom_skill_id || null);
    }
  }, [settingsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!workspace?.id) throw new Error("No workspace");
      const preferences: MeetingAutomationPreferencesUpdate = {
        pre_meeting_enabled: preMeetingEnabled,
        pre_meeting_notify_channel: preMeetingChannel,
        pre_meeting_slack_channel_id:
          preMeetingChannel === "slack_channel" ? preMeetingSlackChannelId || null : null,
        pre_meeting_minutes_before: Number(preMeetingMinutesBefore),
        pre_meeting_include_internal: preMeetingIncludeInternal,
        pre_meeting_guidance: preMeetingGuidance || null,
        pre_meeting_custom_skill_id: preMeetingSkillId || null,

        post_meeting_summary_enabled: postSummaryEnabled,
        post_meeting_summary_channel: postSummaryChannel,
        post_meeting_summary_slack_channel_id:
          postSummaryChannel === "slack_channel" ? postSummarySlackChannelId || null : null,
        post_meeting_summary_custom_skill_id: postSummarySkillId || null,

        post_meeting_followup_enabled: postFollowupEnabled,
        post_meeting_followup_channel: postFollowupChannel,
        post_meeting_followup_slack_channel_id:
          postFollowupChannel === "slack_channel" ? postFollowupSlackChannelId || null : null,
        post_meeting_include_internal: postFollowupIncludeInternal,
        post_meeting_guidance: postFollowupGuidance || null,
        post_meeting_followup_custom_skill_id: postFollowupSkillId || null,
      };
      return updateMeetingAutomationPreferences({
        workspace_id: workspace.id,
        preferences,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["meeting-automation-preferences", workspace?.id],
      });
      notify({
        heading: "Settings saved",
        type: "success",
        messageList: ["Meeting automation settings have been updated."],
      });
    },
    onError: () => {
      notify({
        heading: "Failed to save settings",
        type: "failure",
        messageList: ["Could not update meeting automation settings. Please try again."],
      });
    },
  });

  if (settingsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Meeting Automation</h2>
        <p className="text-sm text-muted-foreground">
          Configure automated briefings, summaries, and follow-ups for your meetings.
        </p>
      </div>

      {/* Pre-meeting briefings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Pre-meeting briefings</CardTitle>
              <CardDescription>
                Get helpful messages before meetings you're attending
              </CardDescription>
            </div>
            <Switch checked={preMeetingEnabled} onCheckedChange={setPreMeetingEnabled} />
          </div>
        </CardHeader>
        {preMeetingEnabled && (
          <CardContent className="space-y-5">
            <NotificationChannelSelector
              value={preMeetingChannel}
              onChange={setPreMeetingChannel}
              slackChannelId={preMeetingSlackChannelId}
              onSlackChannelChange={setPreMeetingSlackChannelId}
            />

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Notify me</label>
              <Select value={preMeetingMinutesBefore} onValueChange={setPreMeetingMinutesBefore}>
                <SelectTrigger className="h-9 w-full max-w-[280px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MINUTES_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">
                  Send for internal meetings
                </p>
                <p className="text-xs text-muted-foreground">
                  Include meetings where all attendees are from your organization
                </p>
              </div>
              <Switch
                checked={preMeetingIncludeInternal}
                onCheckedChange={setPreMeetingIncludeInternal}
              />
            </div>

            <SkillSelector
              value={preMeetingSkillId}
              onChange={setPreMeetingSkillId}
              skills={skills}
              isLoading={skillsQuery.isLoading}
            />

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Additional guidance</label>
              <textarea
                value={preMeetingGuidance}
                onChange={(e) => setPreMeetingGuidance(e.target.value)}
                placeholder="e.g., Focus on recent support tickets and product usage trends for the account."
                rows={3}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </CardContent>
        )}
      </Card>

      {/* Post-meeting summaries */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Post-meeting summaries</CardTitle>
              <CardDescription>
                Get notified with a summary after your meetings finish
              </CardDescription>
            </div>
            <Switch checked={postSummaryEnabled} onCheckedChange={setPostSummaryEnabled} />
          </div>
        </CardHeader>
        {postSummaryEnabled && (
          <CardContent className="space-y-5">
            <NotificationChannelSelector
              value={postSummaryChannel}
              onChange={setPostSummaryChannel}
              slackChannelId={postSummarySlackChannelId}
              onSlackChannelChange={setPostSummarySlackChannelId}
            />

            <SkillSelector
              value={postSummarySkillId}
              onChange={setPostSummarySkillId}
              skills={skills}
              isLoading={skillsQuery.isLoading}
            />
          </CardContent>
        )}
      </Card>

      {/* Post-meeting follow-ups */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">Post-meeting follow-ups</CardTitle>
              <CardDescription>
                Automatically draft follow-up emails and send them to you after meetings
              </CardDescription>
            </div>
            <Switch checked={postFollowupEnabled} onCheckedChange={setPostFollowupEnabled} />
          </div>
        </CardHeader>
        {postFollowupEnabled && (
          <CardContent className="space-y-5">
            <NotificationChannelSelector
              value={postFollowupChannel}
              onChange={setPostFollowupChannel}
              slackChannelId={postFollowupSlackChannelId}
              onSlackChannelChange={setPostFollowupSlackChannelId}
            />

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">
                  Send for internal meetings
                </p>
                <p className="text-xs text-muted-foreground">
                  Include meetings where all attendees are from your organization
                </p>
              </div>
              <Switch
                checked={postFollowupIncludeInternal}
                onCheckedChange={setPostFollowupIncludeInternal}
              />
            </div>

            <SkillSelector
              value={postFollowupSkillId}
              onChange={setPostFollowupSkillId}
              skills={skills}
              isLoading={skillsQuery.isLoading}
            />

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Additional guidance</label>
              <textarea
                value={postFollowupGuidance}
                onChange={(e) => setPostFollowupGuidance(e.target.value)}
                placeholder="e.g., Keep follow-ups concise and include next steps with deadlines."
                rows={3}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </CardContent>
        )}
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="gap-2"
        >
          {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default MeetingAutomationSettingsPage;
